// DirIcon — the Internet OS folder / page / skill mark, drawn INLINE
// (FolderGlyph / FileGlyph / SkillGlyph) from the Figma geometry so it paints
// instantly and tints live to the organism palette. The item's symbol is
// overlaid on the face. A skill is the page with its folded corner turned into
// a spark (law_skill_class): same sheet, same face, same footprint.
//
// ONE FOOTPRINT LAW: folders and pages share the SAME outer box (the folder's
// landscape footprint at `size`), so every grid cell / list row / orbit tile
// aligns identically whichever mark it holds — labels sit on one baseline and
// the icon column never wanders. The page sheet renders slightly NARROWER than
// the folder (a landscape document, centred in the box) so it reads as its own
// thing while occupying the same visual space.
//
// Responsive footprint: the rendered width/height are multiplied by
// --diricon-scale — 0.8 on desktop, 1.1 on mobile. The var is set ONCE per view
// by DirectoryView's <DirIconScaleStyle/> (a media-query rule, flips live, no JS
// / hydration); here we just consume it. Scaling the LAYOUT box (not a
// transform) means the flex column reflows to the real size, so the label below
// stays snug and aligned at either factor. The glyph and overlaid symbol both
// ride the box (100% / em-relative), so every internal proportion is preserved.

import { FileGlyph, FolderGlyph, SkillGlyph } from "./icons";
import type { DirMark } from "./dirMark";
import { GLYPHS } from "../../lib/os/symbols";
import AppleEmoji from "./AppleEmoji";
import { hexToRgb } from "../ColorInstrument";
import { readableOn } from "../../lib/theme";

// A picked emoji is any symbol that isn't one of the geometric marks (symbolFor
// only ever emits GLYPHS members, so a non-member is a hand-picked emoji).
// Emoji render as Apple's own PNG artwork (AppleEmoji) so every platform —
// Android included — sees the same iOS emoji; sized to a constant, smaller
// proportion of the folder and tilted with the same perspective as the flat
// glyphs so it lies on the folder's face instead of floating over it.
const isEmojiSymbol = (s: string) => !!s && !(GLYPHS as readonly string[]).includes(s);

// The scale-factor rule, exported so DirectoryView can mount it exactly once per
// view (rather than once per icon — a media query inside globals.css isn't an
// option, and duplicating the <style> on every icon is wasteful + noisy).
export const DIRICON_SCALE_CSS =
  ".diricon{--diricon-scale:0.76}@media (max-width:640px){.diricon{--diricon-scale:1.05}}";

// The icon-to-label gap for a tile that stacks a DirIcon over its name (FolderItem
// / GridTile). Mobile enlarges the icon via --diricon-scale (0.76 → 1.05) but a
// fixed gap doesn't grow to match — the bigger icon then overlaps a wrapped
// two-line label. `.diricon-gap` scales the gap in step with the same breakpoint
// so there's always clear air between the icon and a wrapped label — desktop
// keeps each caller's original spacing (set via --diricon-gap-base), mobile
// bumps it well past the icon's growth.
export const DIRICON_GAP_CSS =
  ".diricon-gap{gap:var(--diricon-gap-base, 0.5rem)}@media (max-width:640px){.diricon-gap{gap:0.9rem}}";

/** One-shot scoped style for the responsive icon scale + label gap — mount once per view. */
export function DirIconScaleStyle() {
  return <style>{DIRICON_SCALE_CSS + DIRICON_GAP_CSS}</style>;
}

interface DirIconProps {
  icon: DirMark;
  symbol: string;
  size?: number;
  active?: boolean;
  /** per-project colour — locally overrides the organism hue for this folder/page */
  color?: string | null;
  /** what lies on the folder's face in place of the symbol (the memories
   *  folder wears the person's cell avatar there: components/os/Thumb) */
  face?: React.ReactNode;
  /** draw at exactly `size`, ignoring the view's responsive scale (a thumb in a
   *  feed row is sized by its row, not by the directory it is not in) */
  fixed?: boolean;
}

// the folder's landscape aspect — the ONE footprint both marks live in (the
// page now shares the exact same landscape footprint, so they read equal-size)
const BOX_RATIO = 54.3 / 81.6531;

/** readableOn for a stored colour, which is user data and may be malformed. */
function safeReadableOn(hex: string): string {
  try {
    return readableOn(hexToRgb(hex));
  } catch {
    return "#3c342b";
  }
}

export default function DirIcon({ icon, symbol, size = 92, active = false, color, face, fixed = false }: DirIconProps) {
  // The cover now wears the FULL colour, so the mark printed on it has to flip
  // with the cover or it disappears — a dark glyph on a deep purple folder is
  // invisible. A picked colour resolves its ink here; without one the folder
  // rides the organism colour, whose readable ink the theme already publishes
  // as --on-organism.
  const tint = color
    ? ({
        ["--organism-color" as string]: color,
        ["--folder-glyph" as string]: safeReadableOn(color),
      } as React.CSSProperties)
    : ({ ["--folder-glyph" as string]: "var(--on-organism, #3c342b)" } as React.CSSProperties);
  const h = size * BOX_RATIO;
  const emoji = isEmojiSymbol(symbol);
  return (
    <div
      className="diricon"
      style={{
        position: "relative",
        width: `calc(${size}px * var(--diricon-scale, 0.8))`,
        height: `calc(${h}px * var(--diricon-scale, 0.8))`,
        display: "grid",
        placeItems: "center",
        transition: "transform var(--dur) var(--ease-soft)",
        transform: active ? "translateY(-2px) scale(1.02)" : "none",
        ...tint,
        ...(fixed ? { ["--diricon-scale" as string]: 1 } : null),
      }}
      aria-hidden
    >
      {icon === "folder" ? (
        <>
          <FolderGlyph style={{ position: "absolute", inset: 0, width: "100%", height: "100%", zIndex: 1 }} />
          <span
            style={{
              position: "absolute",
              left: "50%",
              top: "57%",
              // BOTH marks lie flat on the tilted folder face — the Apple PNG
              // warps cleanly (it's an image, not a colour font), so the emoji
              // takes the same slight rotateX as the glyphs and reads as printed
              // on the front of the folder rather than hovering upright.
              transform: "translate(-50%, -52%) perspective(230px) rotateX(17deg)",
              transformOrigin: "center",
              fontSize: `calc(${size * 0.235}px * var(--diricon-scale, 0.8))`,
              lineHeight: 1,
              fontWeight: 600,
              zIndex: 2,
              // flat glyphs take the folder-face ink (which flips with the cover
              // colour); the emoji image carries its own colour, so it only needs
              // a whisper of separation from a saturated cover.
              color: "color-mix(in oklab, var(--folder-glyph, #3c342b) 88%, transparent)",
              textShadow: emoji ? "0 1px 2px rgba(0,0,0,0.18)" : "0 0.5px 0 rgba(255,255,255,0.22)",
            }}
          >
            {face ? (
              face
            ) : emoji ? (
              // constant proportion of the folder (smaller than the old text
              // emoji), on every size from list row to the settings preview.
              <AppleEmoji emoji={symbol} size={`calc(${(size * 0.24).toFixed(1)}px * var(--diricon-scale, 0.8))`} />
            ) : (
              symbol
            )}
          </span>
        </>
      ) : (
        // the page shares the folder's FULL footprint (landscape doc), so a file
        // and a folder read as one size and their labels align. A skill is the
        // same page with a spark where the fold was; its face is the page's face.
        <>
          {icon === "skill" ? (
            <SkillGlyph lit={active} style={{ position: "absolute", inset: 0, width: "100%", height: "100%", zIndex: 1 }} />
          ) : (
            <FileGlyph style={{ position: "absolute", inset: 0, width: "100%", height: "100%", zIndex: 1 }} />
          )}
          <span
            style={{
              position: "absolute",
              left: "51.5%",
              top: "56%",
              transform: "translate(-50%, -50%)",
              fontSize: `calc(${size * 0.2}px * var(--diricon-scale, 0.8))`,
              lineHeight: 1,
              zIndex: 2,
              color: emoji ? undefined : "color-mix(in oklab, var(--accent-ink) 78%, transparent)",
            }}
          >
            {emoji ? (
              <AppleEmoji emoji={symbol} size={`calc(${(size * 0.2).toFixed(1)}px * var(--diricon-scale, 0.8))`} />
            ) : (
              symbol
            )}
          </span>
        </>
      )}
    </div>
  );
}
